import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import { useArtikelPositionen, useNaviSuche, useNaviUebersicht, type NaviArtikelTreffer, type NaviStatus, type NaviSuchErgebnis } from '@/api/warehouse'
import { warehouseErrorMessage } from '@/lib/warehouse-company'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { Regalwand, type WandReihe } from './Regalwand'
import { LAGER_2_REIHEN, STATUS_TEXT } from './navi-farben'

const DEBOUNCE_MS = 300

/** Link in die Regale mit markiertem Platz; die Reihe steht vorn im Barcode (R05+F01+P1+EB1). */
export function regaleLink(barcode: string) {
  const reihe = barcode.split('+')[0] ?? ''
  return `/warehouse/lager-navi/regale?reihe=${encodeURIComponent(reihe)}&platz=${encodeURIComponent(barcode)}`
}

/** Lager-Navi Startreiter: ein Suchfeld; leer zeigt es die Belegung aller Reihen. */
export default function SuchePage() {
  const [params, setParams] = useSearchParams()
  const q = params.get('q') ?? ''
  const [eingabe, setEingabe] = useState(q)
  // Zuletzt selbst geschriebenes ?q. Weicht ?q davon ab (Reiter, Menue, Link), gewinnt die URL.
  const gesetzt = useRef(q)

  useEffect(() => {
    if (q === gesetzt.current) return
    gesetzt.current = q
    setEingabe(q)
  }, [q])

  useEffect(() => {
    const neu = eingabe.trim()
    if (neu === q || q !== gesetzt.current) return
    const timer = setTimeout(() => setParams(prev => {
      gesetzt.current = neu
      const next = new URLSearchParams(prev)
      if (neu) next.set('q', neu); else next.delete('q')
      next.delete('artikel')
      return next
    }, { replace: true }), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [eingabe, q, setParams])

  return <div className="px-4 py-4 max-w-7xl mx-auto space-y-5">
    <label className="relative block max-w-xl">
      <span className="sr-only">Lager durchsuchen</span>
      <Search aria-hidden className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted" />
      <Input type="search" value={eingabe} onChange={e => setEingabe(e.target.value)} autoFocus
        placeholder="*16, PL86, P-004082, R05, R26+F09+P1+EB1 oder Name" className="h-11 pl-9 font-mono text-base" />
    </label>
    {q ? <Ergebnis q={q} /> : <Uebersicht />}
  </div>
}

function Ergebnis({ q }: { q: string }) {
  const suche = useNaviSuche(q)
  if (suche.isPending) return <p className="text-muted">Suche „{q}“…</p>
  if (suche.isError) return <p role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm">{warehouseErrorMessage(suche.error)}</p>
  return <Treffer e={suche.data} />
}

function Treffer({ e }: { e: NaviSuchErgebnis }) {
  const leer = (text: string) => <p className="text-sm text-ink-2">{text}</p>
  switch (e.art) {
    case 'palette': {
      const p = e.palette
      if (!p) return leer(`Keine Palette „${e.q}“ gefunden.`)
      return <Karte titel="Palette" kopf={p.palette_no ?? e.q}>
        {p.merchant_sku && <GrundartikelLink code={p.merchant_sku} className="font-mono font-semibold" />}
        {p.name && <p className="text-sm text-ink-2">{p.name}</p>}
        <p className="text-sm tabular-nums">{p.quantity === null ? 'Menge unbekannt' : `${p.circa ? 'ca. ' : ''}${p.quantity} ${p.unit}`}</p>
        {p.barcode ? <Link to={regaleLink(p.barcode)} className="inline-block font-mono font-semibold text-primary underline-offset-2 hover:underline">{p.barcode} im Regal zeigen</Link>
          : <p className="text-sm text-ink-2">Steht auf keinem Platz ({p.state === 'in_transit' ? 'in Bewegung' : p.state}).</p>}
      </Karte>
    }
    case 'platz':
      if (!e.platz) return leer(`Kein Platz „${e.q}“ gefunden.`)
      return <Karte titel="Platz" kopf={e.platz.barcode}>
        {!e.platz.aktiv && <p className="text-sm text-ink-2">Der Platz ist deaktiviert.</p>}
        <Link to={regaleLink(e.platz.barcode)} className="inline-block font-semibold text-primary underline-offset-2 hover:underline">Im Regal {e.platz.reihe} zeigen</Link>
      </Karte>
    case 'reihe':
      if (!e.plaetze) return leer(`Reihe ${e.reihe} hat keine aktiven Plätze.`)
      return <Karte titel="Reihe" kopf={e.reihe}>
        <p className="text-sm text-ink-2">{e.plaetze} aktive Plätze</p>
        <Link to={`/warehouse/lager-navi/regale?reihe=${encodeURIComponent(e.reihe)}`} className="inline-block font-semibold text-primary underline-offset-2 hover:underline">Regal {e.reihe} öffnen</Link>
      </Karte>
    case 'artikel':
      if (!e.artikel.length) return leer(`Keine Treffer für „${e.q}“.`)
      return <ArtikelTreffer treffer={e.artikel} />
  }
}

function ArtikelTreffer({ treffer }: { treffer: NaviArtikelTreffer[] }) {
  const [params, setParams] = useSearchParams()
  const gewaehlt = treffer.length === 1 ? treffer[0]! : treffer.find(t => t.merchant_sku === params.get('artikel'))
  return <div className="space-y-4">
    {treffer.length > 1 && <ul className="divide-y divide-line rounded-xl border border-line bg-white max-w-xl" aria-label="Treffer">
      {treffer.map(t => <li key={t.stock_item_id}>
        <button type="button" disabled={!t.merchant_sku} aria-pressed={gewaehlt?.stock_item_id === t.stock_item_id}
          onClick={() => setParams(prev => { const n = new URLSearchParams(prev); n.set('artikel', t.merchant_sku!); return n }, { replace: true })}
          className={cn('flex w-full items-baseline gap-3 px-3 py-2 text-left hover:bg-primary-soft focus-visible:outline-2 focus-visible:outline-primary',
            gewaehlt?.stock_item_id === t.stock_item_id && 'bg-primary-soft')}>
          <span className="w-20 shrink-0 font-mono font-semibold">{t.merchant_sku ?? '—'}</span>
          <span className="text-sm">{t.name}</span>
        </button>
      </li>)}
    </ul>}
    {gewaehlt && (gewaehlt.merchant_sku ? <ArtikelKarte treffer={gewaehlt} merchantSku={gewaehlt.merchant_sku} />
      : <Karte titel="Artikel" kopf={gewaehlt.sku}>
        <p className="text-sm">{gewaehlt.name}</p>
        <p className="text-sm text-ink-2">ohne Händler-Nr., keine Lagerplätze</p>
      </Karte>)}
  </div>
}

function ArtikelKarte({ treffer, merchantSku }: { treffer: NaviArtikelTreffer; merchantSku: string }) {
  const daten = useArtikelPositionen(merchantSku)
  const s = daten.data?.summary
  const plaetze = (daten.data?.positions ?? []).filter(p => p.placeBarcode && (p.state === 'verschlossen' || p.state === 'ausgepackt'))
  const unterwegs = (daten.data?.positions ?? []).filter(p => p.state === 'in_transit').length
  return <Karte titel="Artikel" kopf={<GrundartikelLink code={merchantSku} className="font-mono" />}>
    <p className="text-sm text-ink-2">{treffer.name}</p>
    {daten.isError && <p role="alert" className="text-sm text-red-700">{warehouseErrorMessage(daten.error)}</p>}
    {daten.isPending && <p className="text-muted text-sm">Lade Bestand…</p>}
    {s && <dl className="grid grid-cols-2 sm:grid-cols-4 gap-px overflow-hidden rounded-lg border border-line bg-line" aria-label="Bestand">
      <Wert label="Gesamt" wert={`${s.gesamt} ${s.unit}`} stark />
      <Wert label="Lager 1" wert={`${s.lager1} ${s.unit}`} />
      <Wert label="Lager 2" wert={`${s.lager2} ${s.unit}`} />
      <Wert label="Packstation" wert={s.packstation ? `${s.packstation.frei} ${s.unit}` : 'nicht gezählt'} />
    </dl>}
    {s?.gesamtUnvollstaendig && <p className="text-xs text-[#7a4b00]">Gesamt unvollständig: nicht jede Palette hat eine bekannte Menge.</p>}
    {daten.data && <div>
      <h4 className="text-[11px] uppercase tracking-wider text-muted mb-1">Plätze ({plaetze.length})</h4>
      {plaetze.length ? <ul className="flex flex-wrap gap-1.5">
        {plaetze.map(p => <li key={p.id}>
          <Link to={regaleLink(p.placeBarcode!)} className="inline-flex items-baseline gap-2 rounded border border-line bg-white px-2 py-1 font-mono text-sm hover:border-[#33414f] focus-visible:outline-2 focus-visible:outline-primary">
            <span className="font-semibold">{p.placeBarcode}</span>
            <span className="text-xs text-ink-2">{p.paletteNo}</span>
          </Link>
        </li>)}
      </ul> : <p className="text-sm text-ink-2">Keine Palette im Regal.</p>}
      {unterwegs > 0 && <p className="text-xs text-ink-2 mt-1">{unterwegs} {unterwegs === 1 ? 'Palette' : 'Paletten'} in Bewegung.</p>}
    </div>}
  </Karte>
}

function Wert({ label, wert, stark }: { label: string; wert: string; stark?: boolean }) {
  return <div className="bg-white px-3 py-2">
    <dt className="text-[11px] uppercase tracking-wider text-muted">{label}</dt>
    <dd className={cn('tabular-nums', stark ? 'text-lg font-bold' : 'font-semibold')}>{wert}</dd>
  </div>
}

function Karte({ titel, kopf, children }: { titel: string; kopf: ReactNode; children: ReactNode }) {
  return <section aria-label={titel} className="max-w-2xl rounded-xl border border-line bg-panel p-4 space-y-2">
    <p className="text-[11px] uppercase tracking-wider text-muted">{titel}</p>
    <h2 className="text-xl font-bold font-mono break-all">{kopf}</h2>
    {children}
  </section>
}

type LagerFilter = 'alle' | '1' | '2'
type ZoneFilter = 'alle' | 'fuss' | 'stapler'

function Uebersicht() {
  const daten = useNaviUebersicht()
  const [lager, setLager] = useState<LagerFilter>('alle')
  const [zone, setZone] = useState<ZoneFilter>('alle')
  const [status, setStatus] = useState<NaviStatus | null>(null)

  const reihen = useMemo(() => {
    const je = new Map<string, WandReihe & { frei_ebene1: number }>()
    for (const r of daten.data?.reihen ?? []) {
      if (lager !== 'alle' && String(r.lager) !== lager) continue
      if (zone !== 'alle' && r.zone !== zone) continue
      const a = je.get(r.reihe) ?? { reihe: r.reihe, lager: r.lager, plaetze: 0, frei: 0, belegt: 0, wartet: 0, notiz: 0, frei_ebene1: 0 }
      a.plaetze += r.plaetze; a.frei += r.frei; a.belegt += r.belegt; a.wartet += r.wartet; a.notiz += r.notiz ?? 0; a.frei_ebene1 += r.frei_ebene1
      je.set(r.reihe, a)
    }
    return [...je.values()].sort((a, b) => a.reihe.localeCompare(b.reihe))
  }, [daten.data, lager, zone])
  const kpi = useMemo(() => {
    const summe = (k: 'plaetze' | 'frei' | 'belegt' | 'wartet' | 'notiz' | 'frei_ebene1') => reihen.reduce((s, r) => s + r[k], 0)
    const plaetze = summe('plaetze'), belegt = summe('belegt')
    return { plaetze, frei: summe('frei'), belegt, wartet: summe('wartet'), notiz: summe('notiz'), frei_ebene1: summe('frei_ebene1'),
      belegung: plaetze ? Math.round(belegt / plaetze * 1000) / 10 : 0 }
  }, [reihen])

  if (daten.isError) return <p role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm">{warehouseErrorMessage(daten.error)}</p>
  if (daten.isPending) return <p className="text-muted">Lade Belegung…</p>
  return <div className="space-y-5">
    <dl aria-label="Kennzahlen" className="flex flex-wrap divide-x divide-line rounded-xl border border-line bg-white">
      <Kennzahl label="Plätze" wert={kpi.plaetze} />
      <Kennzahl label="frei" wert={kpi.frei} />
      <Kennzahl label="belegt" wert={kpi.belegt} />
      <Kennzahl label="Notiz" wert={kpi.notiz} />
      <Kennzahl label="Belegung" wert={`${kpi.belegung.toLocaleString('de-DE')} %`} />
      <Kennzahl label="frei Ebene 1" wert={kpi.frei_ebene1} />
      <Kennzahl label="wartet auf Aufnahme" wert={kpi.wartet} warn={kpi.wartet > 0} />
    </dl>
    <div className="flex flex-wrap gap-x-6 gap-y-2">
      <Filter label="Lager" wert={lager} setze={setLager} optionen={[['alle', 'Alle'], ['1', 'Lager 1'], ['2', `Lager 2 (${LAGER_2_REIHEN})`]]} />
      <Filter label="Zone" wert={zone} setze={setZone} optionen={[['alle', 'Alle'], ['fuss', 'zu Fuß'], ['stapler', 'Stapler']]} />
      <Filter label="Status" wert={status ?? 'alle'} setze={v => setStatus(v === 'alle' ? null : v)}
        optionen={[['alle', 'Alle'], ['frei', 'frei'], ['belegt', 'belegt'], ['wartet', STATUS_TEXT.wartet], ['notiz', 'Notiz']]} />
    </div>
    <Regalwand reihen={reihen} status={status} />
    <p className="text-xs text-muted">Zahl über dem Balken: {status ? STATUS_TEXT[status] : 'freie'} Plätze. Ein Klick öffnet die Reihe.</p>
  </div>
}

function Kennzahl({ label, wert, warn }: { label: string; wert: number | string; warn?: boolean }) {
  return <div className="min-w-28 flex-1 px-4 py-3">
    <dt className="text-[11px] uppercase tracking-wider text-muted">{label}</dt>
    <dd className={cn('text-2xl font-bold tabular-nums font-mono', warn && 'text-[#7a4b00]')}>{wert}</dd>
  </div>
}

function Filter<T extends string>({ label, wert, setze, optionen }: { label: string; wert: T; setze: (v: T) => void; optionen: [T, string][] }) {
  return <div role="group" aria-label={label} className="flex items-center gap-1">
    <span className="mr-1 text-[11px] uppercase tracking-wider text-muted">{label}</span>
    {optionen.map(([v, text]) => <button key={v} type="button" aria-pressed={wert === v} onClick={() => setze(v)}
      className={cn('rounded border px-2 py-1 text-sm focus-visible:outline-2 focus-visible:outline-primary',
        wert === v ? 'border-[#33414f] bg-[#33414f] text-white' : 'border-line bg-white hover:border-[#33414f]')}>
      {text}
    </button>)}
  </div>
}
